import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import { InputContext } from '../chat-input';
import { MessageFileItem } from '../../../type/chat.types';

const FileChip = ({ file }: { file: MessageFileItem }) => {
    const isFileString: boolean = typeof file?.file === 'string';

    const inputHook = useContext(InputContext);
    const { getFileNameFromURL, onGetFileFormat, onDeleteFile, onResendFile } =
        inputHook!;

    const [errorMenu, setErrorMenu] = useState<HTMLButtonElement | null>(null);
    const [fileName, setFileName] = useState('');

    useEffect(() => {
        if (typeof file?.file !== 'string') {
            setFileName(getFileNameFromURL(file) || 'فایل');
        }
    }, [file]);

    return (
        <Box
            sx={{
                height: '32px',
                maxWidth: '120px',
                minWidth: '80px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mr: 0.5,
                border: '1px solid',
                borderColor: 'border.main',
                borderRadius: '16px',
                overflowY: 'hidden',
            }}>
            <Box
                sx={{
                    pl: 1,
                    display: 'flex',
                    alignItems: 'center',
                }}>
                {fileIconGenerator(
                    onGetFileFormat(
                        !isFileString ? file?.file?.name : String(file?.file),
                    ),
                )}
            </Box>

            <Tooltip
                title={
                    <Typography
                        variant="body1"
                        sx={{
                            direction: 'rtl',
                            fontSize: 11,
                            textAlign: 'right',
                        }}>
                        {fileName}
                    </Typography>
                }
                arrow>
                <Typography
                    fontSize={13}
                    sx={{
                        ml: 0.5,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        direction: 'rtl',
                    }}>
                    {fileName}
                </Typography>
            </Tooltip>

            {file?.status === 'loading' ? (
                <Tooltip title="درحال بارگذاری" arrow>
                    <Box sx={{ display: 'flex' }}>
                        <CircularProgress size={18} sx={{ m: 1 }} />
                    </Box>
                </Tooltip>
            ) : file?.status === 'error' ? (
                <>
                    <Tooltip title="ارسال مجدد یا لغو" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setErrorMenu(e.currentTarget)}>
                            <ErrorIcon
                                sx={{ color: 'error.main' }}
                            />
                        </IconButton>
                    </Tooltip>

                    <Menu
                        anchorEl={errorMenu}
                        open={Boolean(errorMenu)}
                        onClose={() => setErrorMenu(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}>
                        <MenuItem
                            sx={{ fontSize: 12, py: 0.2, px: 1 }}
                            onClick={() => {
                                onResendFile(file);
                                setErrorMenu(null);
                            }}>
                            ارسال مجدد
                        </MenuItem>
                        <MenuItem
                            sx={{ fontSize: 12, py: 0.2, px: 1 }}
                            onClick={() => {
                                onDeleteFile(Number(file?.local_id));
                                setErrorMenu(null);
                            }}>
                            لغو ارسال
                        </MenuItem>
                    </Menu>
                </>
            ) : (
                <Tooltip title="حذف" arrow>
                    <IconButton
                        size="small"
                        onClick={() => {
                            onDeleteFile(Number(file?.local_id));
                            setErrorMenu(null);
                        }}>
                        <CancelIcon
                            sx={{ color: 'text.secondary' }}
                        />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default FileChip;

// generate the icon of file type
const fileIconGenerator = (fileType: string | undefined) => {
    switch (fileType) {
        case 'png':
        case 'jpg':
        case 'jpeg':
            return <ImageOutlinedIcon sx={{ width: 18, height: 18 }} />;
        case 'pdf':
            return <PictureAsPdfIcon sx={{ width: 18, height: 18 }} />;
        case 'docx':
            return <ArticleIcon sx={{ width: 18, height: 18 }} />;
        default:
            return (
                <InsertDriveFileOutlinedIcon sx={{ width: 18, height: 18 }} />
            );
    }
};
